import ServiceTemplate from '../../../templates/service/ServiceTemplate';
import type { ServiceConfig } from '../../../templates/service/types';

const config: ServiceConfig = {
  pageTitle: 'Electrical Repair & Maintenance Sleaford | Quantum Electrical Services',
  metaDescription: 'Fast, reliable electrical repair & maintenance in Sleaford, Lincolnshire. Fault finding, emergency electrician, circuit repairs and more. NAPIT registered. Call 01529 265060.',
  keywords: 'electrical repair Sleaford, electrician repair Sleaford, electrical fault finding Sleaford, emergency electrician Sleaford Lincolnshire, electrical maintenance Sleaford, circuit repair Sleaford',
  canonicalPath: '/services/repair-maintenance',

  heroBadge: 'NAPIT Registered · Sleaford & Lincolnshire',
  heroTitle: 'Electrical Repair &',
  heroHighlight: 'Maintenance',
  heroLocation: 'Sleaford, Lincolnshire',
  heroDesc: 'Electrical problems resolved quickly, safely and permanently. Quantum Electrical provides expert fault finding, circuit repairs, emergency call-outs and ongoing maintenance across Sleaford and Lincolnshire — NAPIT registered, fully insured, and with no hidden call-out surprises.',
  heroImageUrl: 'https://public.readdy.ai/ai/img_res/8e910b4fdc7f23ffe906fdb327f31288.jpg',

  servicesHeading: 'Every Repair & Maintenance Service We Offer',
  servicesSubheading: 'From a tripped circuit to a complete fault investigation — Quantum Electrical resolves all electrical problems across Sleaford and Lincolnshire quickly and correctly.',
  services: [
    { icon: 'ri-search-eye-fill', title: 'Fault Finding & Diagnosis', desc: 'Systematic electrical fault diagnosis using professional test equipment — problems found and fixed in Sleaford.' },
    { icon: 'ri-alarm-warning-fill', title: 'Emergency Electrical Call-Out', desc: 'Urgent electrical faults and failures attended promptly across Sleaford and the surrounding Lincolnshire area.' },
    { icon: 'ri-plug-fill', title: 'Socket & Switch Repairs', desc: 'Faulty, broken or dangerous sockets and switches repaired or replaced throughout Sleaford properties.' },
    { icon: 'ri-lightbulb-flash-fill', title: 'Lighting Faults & Repairs', desc: 'Flickering lights, dead circuits, failed fittings and wiring faults — diagnosed and resolved in Sleaford.' },
    { icon: 'ri-dashboard-fill', title: 'Consumer Unit Faults', desc: 'Tripping breakers, faulty RCDs and consumer unit faults investigated and resolved in Sleaford.' },
    { icon: 'ri-flashlight-fill', title: 'Circuit Repairs & Restoration', desc: 'Damaged or failed circuits repaired and restored to full operation across Lincolnshire properties.' },
    { icon: 'ri-temp-hot-fill', title: 'Heating & Boiler Wiring Repairs', desc: 'Faulty programmer wiring, broken controls and heating circuit faults repaired in Sleaford.' },
    { icon: 'ri-home-fill', title: 'Storm & Water Damage', desc: 'Electrical damage caused by flooding, storm or moisture assessed and repaired safely in Lincolnshire.' },
    { icon: 'ri-building-fill', title: 'Commercial Electrical Maintenance', desc: 'Planned and reactive electrical maintenance contracts for commercial premises in Sleaford and Lincolnshire.' },
    { icon: 'ri-tools-fill', title: 'Appliance Connection Repairs', desc: 'Faulty cooker, shower and appliance circuit connections diagnosed and repaired in Sleaford.' },
    { icon: 'ri-road-map-fill', title: 'Outdoor & Garden Electrical Repairs', desc: 'Failed garden sockets, broken outdoor lighting and damaged outdoor circuits repaired across Lincolnshire.' },
    { icon: 'ri-wifi-fill', title: 'Smart Home Fault Diagnosis', desc: 'Smart switches, automations and connected devices diagnosed and repaired throughout Sleaford.' },
  ],

  whyUs: [
    {
      icon: 'ri-time-fill',
      title: 'Fast Response',
      desc: 'We respond promptly to all repair and fault calls in Sleaford — no waiting weeks to get an electrician.',
    },
    {
      icon: 'ri-shield-check-fill',
      title: 'NAPIT Registered',
      desc: 'All repair and maintenance work in Sleaford is carried out by our NAPIT registered, fully qualified electricians.',
    },
    {
      icon: 'ri-search-eye-fill',
      title: 'Root Cause Fixed',
      desc: 'We find and fix the root cause, not just the symptom — no repeat call-outs for the same fault.',
    },
    {
      icon: 'ri-coin-fill',
      title: 'Transparent Pricing',
      desc: 'Clear pricing discussed before we start — no shock bills and no hidden call-out charges in Sleaford.',
    },
  ],

  processSteps: [
    {
      number: '01',
      icon: 'ri-phone-fill',
      title: 'Call Us',
      desc: 'Describe the fault — call 01529 265060 for urgent issues or message for non-emergency repairs in Sleaford.',
    },
    {
      number: '02',
      icon: 'ri-search-eye-fill',
      title: 'Diagnose',
      desc: 'We attend your Sleaford property and use professional test equipment to pinpoint the exact fault.',
    },
    {
      number: '03',
      icon: 'ri-tools-fill',
      title: 'Repair',
      desc: 'The fault is repaired correctly and permanently — using quality parts and proper methods.',
    },
    {
      number: '04',
      icon: 'ri-file-certificate-fill',
      title: 'Test & Certify',
      desc: 'All repaired circuits are tested and a Minor Works Certificate issued where required.',
    },
  ],

  gallery: [
    {
      src: 'https://readdy.ai/api/search-image?query=professional%20electrical%20testing%20multimeter%20probe%20testing%20circuit%20UK%20domestic%20property%20dark%20amber%20background%20no%20people%20close%20up%20diagnostic%20tools%20professional%20quality%20Lincolnshire%20fault%20finding%20equipment&width=800&height=600&seq=gal-rep-01-v1&orientation=landscape',
      alt: 'Electrical fault finding Sleaford',
      caption: 'Fault Finding — Sleaford',
      label: 'Fault Diagnosis',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=tripped%20RCD%20circuit%20breaker%20consumer%20unit%20UK%20domestic%20property%20close%20up%20detail%20electrician%20investigating%20fault%20no%20people%20Lincolnshire%20Sleaford%20amber%20warm%20light%20professional%20electrical%20safety&width=600&height=500&seq=gal-rep-02-v1&orientation=squarish',
      alt: 'Consumer unit fault investigation Sleaford',
      caption: 'Consumer Unit Fault — Diagnosed',
      label: 'Consumer Unit',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=faulty%20damaged%20electrical%20socket%20being%20repaired%20replaced%20UK%20domestic%20property%20professional%20electrician%20work%20close%20up%20no%20people%20Lincolnshire%20Sleaford%20neat%20repair%20work&width=600&height=500&seq=gal-rep-03-v1&orientation=squarish',
      alt: 'Socket repair Sleaford',
      caption: 'Socket Repair — Sleaford',
      label: 'Socket Repair',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=professional%20electrical%20insulation%20resistance%20test%20equipment%20IR%20tester%20connected%20to%20wiring%20UK%20domestic%20Lincolnshire%20no%20people%20close%20up%20testing%20instrument%20leads%20circuit%20diagnostic&width=600&height=500&seq=gal-rep-04-v1&orientation=squarish',
      alt: 'Circuit testing Sleaford Lincolnshire',
      caption: 'Circuit Testing — Lincolnshire',
      label: 'Circuit Test',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=outdoor%20garden%20socket%20repair%20replacement%20UK%20domestic%20property%20exterior%20wall%20weatherproof%20fitting%20professional%20electrician%20work%20Lincolnshire%20Sleaford%20no%20people%20neat%20finish&width=600&height=500&seq=gal-rep-05-v1&orientation=squarish',
      alt: 'Outdoor electrical repair Sleaford',
      caption: 'Outdoor Repair — Sleaford',
      label: 'Outdoor Repair',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=electrical%20lighting%20circuit%20fault%20repair%20ceiling%20rose%20junction%20box%20UK%20domestic%20property%20professional%20repair%20work%20no%20people%20Lincolnshire%20Sleaford%20close%20up%20wiring%20detail%20amber%20light&width=600&height=500&seq=gal-rep-06-v1&orientation=squarish',
      alt: 'Lighting circuit repair Sleaford',
      caption: 'Lighting Repair — Lincolnshire',
      label: 'Lighting Repair',
    },
  ],

  faqHeading: 'Repair & Maintenance Questions',
  faqSubheading: 'Common questions about our electrical repair and maintenance services in Sleaford and across Lincolnshire.',
  faqs: [
    {
      q: 'How quickly can you respond to an electrical fault in Sleaford?',
      a: 'For urgent electrical faults in Sleaford, we aim to respond as quickly as possible — often the same day or next day for most calls. For genuine emergencies (such as complete power failure, burning smells, sparking, or exposed live conductors), call us immediately on 01529 265060 and we\'ll prioritise your call. We always aim to attend Sleaford properties quickly because electrical faults can be dangerous if left.',
    },
    {
      q: 'Why does my RCD keep tripping in my Sleaford home?',
      a: 'A repeatedly tripping RCD in your Sleaford home usually indicates a fault somewhere on the circuits it protects. The most common causes are: a failing appliance drawing earth leakage current, damaged wiring with a breakdown in insulation, moisture ingress in an outdoor circuit or bathroom fitting, or a failing RCD device itself. We carry out systematic fault finding using professional test equipment to isolate the exact cause and repair it correctly.',
    },
    {
      q: 'Is it safe to use my electrics if a circuit keeps tripping in Sleaford?',
      a: 'If a circuit is repeatedly tripping in your Sleaford home, this is a warning sign of an underlying electrical fault and should be investigated promptly. While a single isolated trip may be caused by a simple overload (too many appliances on one circuit), repeated tripping indicates a genuine fault. Do not attempt to tape up or bypass circuit protection — contact us to diagnose the cause correctly and safely.',
    },
    {
      q: 'How much does an electrical fault finding visit cost in Sleaford?',
      a: 'Our fault finding visits in Sleaford are priced transparently — we discuss costs before attending and there are no hidden surprises. A standard fault finding visit starts from £80–£150 depending on the complexity of the fault. For straightforward faults resolved in a single visit, the repair is included in the diagnostic fee wherever possible. We always confirm pricing before proceeding with any work.',
    },
    {
      q: 'Can you repair electrical storm or water damage in Sleaford?',
      a: 'Yes — storm and water damage can cause significant electrical problems including failed circuits, damaged consumer units, corrosion in fittings and broken outdoor installations. We carry out thorough electrical inspections and repairs following storm or flood damage across Sleaford and Lincolnshire. All damaged wiring and fittings are identified, replaced and fully tested before reinstatement.',
    },
    {
      q: 'Do you offer electrical maintenance contracts in Sleaford?',
      a: 'Yes — for commercial premises and landlords in Sleaford and Lincolnshire, we offer planned maintenance contracts covering periodic inspection, testing and reactive fault response. Planned maintenance significantly reduces the risk of unexpected electrical failures and ensures compliance with current regulations. Contact us to discuss a maintenance package tailored to your Sleaford property portfolio or commercial premises.',
    },
    {
      q: 'Will I receive documentation after an electrical repair in Sleaford?',
      a: 'Yes — for minor electrical repair work, we issue a Minor Electrical Installation Works Certificate (MEIWC) confirming the work carried out and test results. For more extensive repairs involving new wiring or circuits, a full Electrical Installation Certificate (EIC) is issued. All repair work carried out in Sleaford is properly documented — essential for insurance purposes and your records.',
    },
    {
      q: 'Can you repair outdoor electrical damage in Sleaford gardens?',
      a: 'Yes — we repair all types of outdoor electrical damage in Sleaford including failed garden sockets, broken security lights, storm-damaged garden lighting circuits, tripped outdoor RCDs and damaged cable runs. All outdoor repair work is correctly weatherproofed and tested before we leave. If the damage is extensive, we may recommend a full replacement of the affected circuit rather than a repair.',
    },
  ],

  mapHeading: 'Repairs & Maintenance Across Sleaford & Lincolnshire',
  mapSubheading: 'Fast, reliable electrical repairs for homes and businesses throughout Sleaford and all of Lincolnshire.',
  mapEmbedSrc: 'https://maps.google.com/maps?q=Sleaford,Lincolnshire,UK&t=&z=12&ie=UTF8&iwloc=&output=embed',
  mapAreaList: ['Sleaford', 'Grantham', 'Boston', 'Spalding', 'Lincoln', 'Newark-on-Trent', 'Stamford', 'Bourne', 'Heckington', 'Ruskington', 'Ancaster', 'Cranwell', 'Leadenham', 'Navenby', 'Metheringham', 'Kirkby la Thorpe'],

  formUrl: 'https://readdy.ai/api/form/d73s2osprit363gl0dfg',
};

export default function RepairMaintenancePage() {
  return <ServiceTemplate config={config} />;
}
